// Types
import { Order } from "@/types/Order";

// Libs
import { useState, useCallback } from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/atoms/select";

// Hooks
import { useOrdersApi } from "@/hooks/useOrdersApi";

// Components
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogClose,
} from "@/components/atoms/dialog";
import { Button } from "@/components/atoms/button";

// Icons
import { X, CheckCircle } from "lucide-react";

interface OrderCancellationModalProps {
  trigger: React.ReactNode;
  order: Order | null;
  onCancellationSuccess?: (orderId: number) => void;
  onOrderStatusUpdate: (orderId: number, newStatus: string) => void;
}

// Límite de caracteres para descripción
const CANCEL_DESCRIPTION_MAX_CHARS = 200;

export default function OrderCancellationModal({
  trigger,
  order,
  onCancellationSuccess,
  onOrderStatusUpdate,
}: OrderCancellationModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusType, setStatusType] = useState<"success" | "error" | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [reasonCode, setReasonCode] = useState<string | "">("");
  const [reasonDescription, setReasonDescription] = useState<string>("");
  const { updateOrderStatus, fetchCancellationTypes } = useOrdersApi();
  const [cancelTypes, setCancelTypes] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  // Nueva función para limitar la descripción al máximo permitido
  const handleReasonDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const newValue = e.target.value;
    if (newValue.length <= CANCEL_DESCRIPTION_MAX_CHARS) {
      setReasonDescription(newValue);
    } else {
      // Opcional: impide que el usuario siga escribiendo más de lo permitido
      setReasonDescription(newValue.slice(0, CANCEL_DESCRIPTION_MAX_CHARS));
    }
  };

  const loadCancellationTypes = useCallback(async () => {
    try {
      const list = await fetchCancellationTypes();
      setCancelTypes(list);
    } catch (_e) {
      // error handled in hook toast
    }
  }, [fetchCancellationTypes]);

  const handleCancel = async () => {
    if (!order) return;

    // Require reason code
    if (reasonCode === "") {
      setErrorMessage("Selecciona un motivo de cancelación");
      setStatusType("error");
      return;
    }

    // Validación frontend por si se forzó por consola un texto más largo
    if (reasonDescription.length > CANCEL_DESCRIPTION_MAX_CHARS) {
      setErrorMessage(
        `La descripción no puede tener más de ${CANCEL_DESCRIPTION_MAX_CHARS} caracteres`,
      );
      setStatusType("error");
      return;
    }

    setIsProcessing(true);
    try {
      await updateOrderStatus(order.id, "cancelled", {
        type: reasonCode,
        reason: reasonDescription || undefined,
      });
      setStatusType("success");
      onOrderStatusUpdate(order.id, "cancelled");
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : "Error desconocido al cancelar la orden";
      setErrorMessage(errorMsg);
      setStatusType("error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    // Si el modal se está cerrando después de un éxito, llamar al callback de actualización
    if (statusType === "success" && onCancellationSuccess && order) {
      onCancellationSuccess(order.id);
    }

    setStatusType(null);
    setErrorMessage("");
    setIsProcessing(false);
  };

  if (!order) return null;

  const displayId = order.tracking_id || `#${order.id}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) {
          loadCancellationTypes();
        } else {
          handleClose();
        }
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="max-w-md w-full p-0 rounded-2xl overflow-hidden bg-white [&>button]:hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle className="sr-only">
          Confirmar cancelación de orden
        </DialogTitle>
        <DialogDescription className="sr-only">
          Modal de confirmación para cancelar una orden del sistema
        </DialogDescription>

        {!statusType ? (
          <div className="flex flex-col items-center justify-center p-6 relative">
            {/* Icon Circle */}
            <div className="mb-4">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-500">
                  <X className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>

            {/* Title and Description */}
            <div className="text-xl font-semibold mb-2 text-center text-gray-900">
              ¿Estás seguro que deseas cancelar esta orden?
            </div>
            <div className="text-center text-gray-600 mb-2">
              <span className="font-medium">ID: {displayId}</span>
            </div>
            <div className="text-center text-gray-600 mb-6 px-4">
              Esta acción no se puede deshacer. La orden será marcada como
              cancelada.
            </div>

            {/* Reason Select */}
            <div className="w-full mb-4 text-left">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo de cancelación
              </label>
              {cancelTypes.length === 0 ? (
                <div className="text-gray-500 text-sm p-2 italic">
                  {open
                    ? "Cargando motivos de cancelación..."
                    : "No hay motivos de cancelación disponibles"}
                </div>
              ) : (
                <Select
                  value={reasonCode === "" ? "" : String(reasonCode)}
                  onValueChange={(val) => setReasonCode(val || "")}
                  disabled={isProcessing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {cancelTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type
                          .toLowerCase()
                          .replace(/_/g, " ")
                          .charAt(0)
                          .toUpperCase() +
                          type.toLowerCase().replace(/_/g, " ").slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Description */}
            <div className="w-full mb-4 text-left">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción (opcional)
              </label>
              <div className="relative">
                <textarea
                  className="w-full border border-gray-200 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-14"
                  rows={3}
                  placeholder={`Agrega detalles de la cancelación (máx. ${CANCEL_DESCRIPTION_MAX_CHARS} carácteres)`}
                  value={reasonDescription}
                  maxLength={CANCEL_DESCRIPTION_MAX_CHARS}
                  onChange={handleReasonDescriptionChange}
                  disabled={isProcessing}
                />
                <span
                  className={`absolute bottom-2 right-3 text-xs ${
                    reasonDescription.length >= CANCEL_DESCRIPTION_MAX_CHARS
                      ? "text-red-500 font-semibold"
                      : "text-gray-400"
                  }`}
                >
                  {reasonDescription.length}/{CANCEL_DESCRIPTION_MAX_CHARS}
                </span>
              </div>
            </div>

            {/* Error message for user, if any */}
            {errorMessage && statusType === "error" && (
              <div className="w-full mb-2">
                <div className="text-red-600 text-sm">{errorMessage}</div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 w-full">
              <DialogClose asChild>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={isProcessing}
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Abortar
                </Button>
              </DialogClose>

              <Button
                onClick={handleCancel}
                disabled={
                  isProcessing ||
                  reasonDescription.length > CANCEL_DESCRIPTION_MAX_CHARS
                }
                className="flex-1 text-white bg-red-500 hover:bg-red-600"
              >
                {isProcessing ? "Cancelando..." : "Sí, cancelar orden"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-6">
            {statusType === "success" ? (
              <>
                {/* Success Icon */}
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-500">
                      <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>

                {/* Success Message */}
                <div className="text-xl font-semibold mb-2 text-center text-gray-900">
                  ¡Orden cancelada exitosamente!
                </div>
                <div className="text-center text-gray-600 mb-6">
                  La orden {displayId} ha sido cancelada correctamente.
                </div>
              </>
            ) : (
              <>
                {/* Error Icon */}
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-500">
                      <X className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                <div className="text-xl font-semibold mb-2 text-center text-gray-900">
                  Error al cancelar la orden
                </div>
                <div className="text-center text-gray-600 mb-6 px-4">
                  {errorMessage ||
                    "Hubo un problema al cancelar la orden. Por favor, inténtalo de nuevo."}
                </div>
              </>
            )}
            <DialogClose asChild className="w-full">
              <Button
                variant="outline"
                className="w-full bg-gray-900 hover:bg-gray-800 text-white"
                disabled={isProcessing}
              >
                Cerrar
              </Button>
            </DialogClose>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
